import React, { useState } from "react";
import { Sparkles, Loader2, Wand2, Scissors, TrendingUp, RefreshCw, CheckCheck } from "lucide-react";
import { aiAssist } from "../api";
import { useToast } from "../hooks/use-toast";

// Reusable inline AI writing helper. Drop it above any text field and pass the
// current value + an onChange callback; it writes the AI result straight back.
const ACTIONS = [
  { key: "draft", label: "Draft", icon: Sparkles, needsText: false },
  { key: "improve", label: "Improve", icon: Wand2, needsText: true },
  { key: "shorten", label: "Shorten", icon: Scissors, needsText: true },
  { key: "persuasive", label: "Persuasive", icon: TrendingUp, needsText: true },
  { key: "regenerate", label: "Regenerate", icon: RefreshCw, needsText: true },
  { key: "proofread", label: "Proofread", icon: CheckCheck, needsText: true },
];

export const AiAssist = ({ value = "", onChange, context = "", channel = "email", tone = "", className = "", testid = "ai-assist" }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(null);

  const run = async (action) => {
    setBusy(action);
    try {
      const r = await aiAssist({ action, text: value || "", context, channel, tone });
      if (r?.text) {
        onChange(r.text);
        toast({ title: "AI updated your draft" });
      }
    } catch (e) {
      toast({ title: "AI assistant unavailable", description: e?.response?.data?.detail || "Please try again." });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={`flex items-center gap-1.5 flex-wrap ${className}`} data-testid={testid}>
      <span className="inline-flex items-center gap-1 text-[11px] font-500 text-[#b8935f] pr-1">
        <Sparkles size={13} /> AI
      </span>
      {ACTIONS.map(({ key, label, icon: Icon, needsText }) => (
        <button
          key={key}
          type="button"
          disabled={!!busy || (needsText && !(value || "").trim())}
          onClick={() => run(key)}
          data-testid={`${testid}-${key}`}
          title={needsText && !(value || "").trim() ? "Write something first" : label}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#e2d8c8] text-[11px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-white"
        >
          {busy === key ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />} {label}
        </button>
      ))}
    </div>
  );
};

export default AiAssist;
